import React from 'react';
import { useIntl } from 'react-intl';
import { useT } from '@transifex/react';
import Grid from '@mui/material/Grid';
import { Button, Typography } from '@raisin/components-lib';
import { getDistributor } from '@raisin/cp-utils';
import { DBSProduct } from 'types';
import { PRODUCT_TYPES } from 'utils/BIPSMappingToOBS/deposits';
import messages from '../../messages';
import { formatDefaultRate, formatPromotedProductTerm } from 'utils/bankPageUtils/bankOffers';
import { BANK_PAGES, BANK_EVENTS, trackTopOfferClick } from 'eventsTracking';
import { getMarketingProductType } from 'utils/getMarketingProductType';
import * as S from './styles';

export interface BankBestOfferProps {
  offer: DBSProduct | undefined;
  testId?: string;
  offerPageUrl?: string;
  onBankBestOfferClick?: Function;
}

const BankBestOffer: React.FC<BankBestOfferProps> = ({
  testId = 'promoted-product',
  offer,
  offerPageUrl,
  onBankBestOfferClick,
}) => {
  const t = useT();
  const intl = useIntl();
  const { locale, formatNumber } = intl;
  const distributorId = getDistributor();

  const productTypeContent = {
    [PRODUCT_TYPES.TERM]: {
      duration: messages.promotedProductTermHeadline,
      value: offer,
    },
    [PRODUCT_TYPES.OVERNIGHT]: {
      duration: messages.promotedProductOvernightHeadline,
      value: offer,
    },
    [PRODUCT_TYPES.NOTICE]: {
      duration: messages.promotedProductNoticeHeadline,
      value: offer,
    },
  };

  const handleClick = (e) => {
    if (offer && onBankBestOfferClick) {
      onBankBestOfferClick(offer);
    }
    e.stopPropagation();
    trackTopOfferClick({
      eventName: BANK_EVENTS.TOP_OFFER_CLICKED,
      bank_id: offer?.deposit_taking_bank?.id,
      bankPage: BANK_PAGES.DETAIL_PAGE,
      distributorId,
      interestRates: {
        interest_rate_effective: offer?.interest_rates?.interest_rate_effective,
        interest_rate_nominal: offer?.interest_rates?.interest_rate_nominal,
      },
      product_type: offer && getMarketingProductType(offer),
      productId: offer?.id,
    });
  };

  const renderContent = () => {
    const content = offer ? productTypeContent[getMarketingProductType(offer)] : null;
    if (content) {
      return (
        <S.SubText variant="subtitle1" component="p" data-testid="duration-and-value">
          <S.SubTextHeader variant="subtitle1">{t(content.duration)}: </S.SubTextHeader>
          {formatPromotedProductTerm(content.value, intl, t)}
        </S.SubText>
      );
    }
    return null;
  };

  return (
    <S.Card data-testid={testId}>
      <S.GridContainer>
        <Grid item xs={12}>
          <Typography variant="h4" textAlign="center">
            {t(messages.bankBestOfferTitle)}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <S.InterestRate variant="h2" component="p" textAlign="center">
            {formatDefaultRate(offer, locale, formatNumber, t)}
          </S.InterestRate>
        </Grid>
        <Grid item xs={12}>
          {renderContent()}
        </Grid>
      </S.GridContainer>
      <Button
        data-testid="to-the-offer"
        size="medium"
        mode="primary"
        fullWidth
        sx={{ mt: 'auto' }}
        href={offerPageUrl}
        // @ts-ignore
        onClick={(e) => handleClick(e)}
      >
        {t(messages.bankBestOfferButton)}
      </Button>
    </S.Card>
  );
};

export default BankBestOffer;
